<script setup lang="ts">
import './account_picker.scss'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import _ from 'lodash'
import { computed, onMounted, ref, toRaw, watch } from 'vue'
import { uuid } from 'vue3-uuid'
import { Icon } from '@iconify/vue'
import AccountAPI from '@/modules/account/constants/account.api.uri.const'
import CsAvatar from '@/onenetwork/components/csimage/CsAvatar.vue'
import { getFirstCharacterName } from '@/utils/helper.ts'
import Paginator from '@/onenetwork/components/paginator/Paginator.vue'

interface Props {
  placeholder?: string,
  type?: string,
  multiple?: boolean,
  modelValue?: any
  readOnly?: boolean
  account?: object | null
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue', 'update:selected'])

const keywordInput = ref<HTMLInputElement>(null)
const queryResultPanel = ref<HTMLUListElement>(null)
const showQueryPanel = ref(false)
const previewValue = ref('')
const selectedValue = ref<any>([])
const queryResult = ref<any>([])
const selectedIndex = ref(0)
const loading = ref(false)
const readOnly = computed(() => props.readOnly)

const handleClickOutside = (e: MouseEvent) => {
  if( !keywordInput.value?.contains(e.target as Node) ) {

    showQueryPanel.value = false
    queryResult.value = []
    selectedIndex.value = 0
  }
}

const handleClick = () => {

  showQueryPanel.value = true
}

const parseResult = (rawData: any) => {
  if(rawData.data && rawData.data?.length > 0) {
    return rawData.data.map((item: any) => {
      return {
        id: item.id,
        name: item.full_name,
        avt: item.avatar?.url?.thumbnail,
        phone: item.phone_number,
        code: item.code,
        customer: item.customer_profile,
        broker: item.broker_profile,
        select: false
      }
    })
  }
  return []
}

const getQueryUri = () => {
  switch (props.type) {
    case 'broker': return 'accounts/query'
    case 'customer': return 'accounts/query'
    default: return 'accounts/query'
  }
}

const getAccountType = () => {
  switch (props.type) {
    case 'broker': return 'broker'
    case 'customer': return 'customer'
    default: return ''
  }
}

const _page = ref(1)
const totalPage = ref(0)
const totalRecords = ref(0)
const _keyword = ref('')

const requestFetchQueryResult = async () => {
  try{
    loading.value = true

    const res = await APIClient.get(getQueryUri(), {
      params: {
        page: _page.value,
        page_size: 10,
        keyword: _keyword.value,
        account_type: getAccountType()
      }
    });

    totalRecords.value = res.data.data.total;
    totalPage.value = res.data.data.to;
    queryResult.value = parseResult(res.data.data);
  } catch (e) {

  } finally {
    loading.value = false
  }
}

const handleChangePage = (page: number) => {
  _page.value = page
  requestFetchQueryResult()
}

const debouncedRequest = _.debounce((value: string) => {

  _keyword.value = value;
  requestFetchQueryResult();
}, 500);


const moveSelection = (direction: 'up' | 'down') => {
  const list = queryResult.value;

  let index = list.findIndex(item => item.select);

  // Nếu chưa có item nào được chọn
  if (index === -1) {
    index = direction === 'down' ? 0 : list.length - 1;
  } else {
    list[index].select = false; // bỏ chọn hiện tại

    index = direction === 'up'
      ? (index - 1 + list.length) % list.length
      : (index + 1) % list.length;
  }

  list[index].select = true;

  selectedIndex.value = index;
  queryResult.value = list;
};


const handleQuery = (e: KeyboardEvent) => {
  // console.log('handleQuery:', e);
  if(!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
    const target = e.target as HTMLInputElement;
    const value = target?.value || '';

    if (value.length > 2) {
      debouncedRequest(value);
    }
  }
  else{
    switch(e.key) {
      case 'ArrowDown': moveSelection('down'); break;
      case 'ArrowUp': moveSelection('up'); break;
      case 'Enter': handleSelect(); break;
    }
  }
};

const handleSelect = (item: any | undefined = undefined) => {

  const _item = item || queryResult.value[selectedIndex.value]

  changeSelectedValue(_item)
  showQueryPanel.value = false
}


const changeSelectedValue = (_item: any | undefined = undefined) => {

  if (!_item) return;
  if (props.multiple) {
    const exists = selectedValue.value.some((v: any) => v.id === _item.id);
    if (!exists) {
      selectedValue.value.push(_item);
    }
  } else {
    selectedValue.value = [_item];
  }
  emit('update:selected', toRaw(selectedValue.value) );
  emit('update:modelValue', toRaw(selectedValue.value) );
}

const removeSelectedValue = (_item: any | undefined = undefined) => {
  if (!_item) return;
  selectedValue.value = selectedValue.value.filter((v: any) => v.id !== _item.id);
  emit('update:selected', toRaw(selectedValue.value) );
  emit('update:modelValue', toRaw(selectedValue.value) );
};

watch(()=>props.account, (newVal, oldVal) => {
  if(newVal) {
    // console.log(198, props.account, newVal, oldVal);
    if (!_.isEqual(newVal, oldVal)) {
      selectedValue.value = [{
        id: newVal.id,
        name: newVal.full_name || newVal.name,
        avt: newVal.avatar?.url?.thumbnail || newVal.avt,
        phone: newVal.phone_number || newVal.phone,
        code: newVal.code,
        customer: newVal.customer_profile,
        broker: newVal.broker_profile,
        select: true
      }];
     }
  }
}, {immediate: true, deep: true});

// onMounted(() => {
//   if(props.account) {
//     selectedValue.value = [{
//       id: props.account?.id,
//       name: props.account?.full_name,
//       avt: props.account?.avatar?.url?.thumbnail,
//       phone: props.account?.phone_number,
//       code: props.account?.code,
//       customer: props.account?.customer_profile,
//       broker: props.account?.broker_profile,
//       select: true
//     }];
//   }
// })

watch(()=>selectedValue, (newVal, oldVal) => {
  console.log(230, newVal?.value, oldVal?.value);
}, {immediate: true, deep: true});
</script>

<template>
  <div class="account-picker">
    <div ref="keywordInput" :class="`account-picker__preview flex gap-2 ${selectedValue.length > 0 ? 'has-value' : ''}`" @click="handleClick">
      <template v-if="selectedValue.length > 0">
        <div class="account-picker__preview_item flex" v-for="(item, idx) in selectedValue" :key="`tag-item-${idx}`">
          <span class="thumb">
            <cs-avatar :src="item.avt" alt="" :label="getFirstCharacterName(item.name)"/>
          </span>
          <span class="name">
            {{ item.name }}
          </span>
          <span class="close" @click="()=>removeSelectedValue(item)" v-if="!readOnly">
            <Icon icon="material-symbols:close" />
          </span>
        </div>
      </template>
      <template v-else>
        <div class="account-picker__preview_item flex w-full">
          <span>{{ props.placeholder || 'Chọn tài khoản' }}</span>
        </div>
      </template>
    </div>
    <div class="account-picker__query" v-if="!readOnly && showQueryPanel" v-click-outside="handleClickOutside">
      <div class="flex flex-col">
        <span class="text-[11px] font-italic">Tìm kiếm:</span>
        <input type="text" class="account-picker__query_keyword" @keyup="handleQuery"/>
      </div>
      <div class="flex flex-col mt-3">
        <span class="text-[11px] font-italic">Kết quả:</span>
        <ul class="account-picker__query_result" ref="queryResultPanel" v-if="queryResult.length > 0">
          <li v-for="(item, idx) in queryResult" :key="`item-query-result-${idx}`" :class="item.select ? 'selected' : ''" @click="()=>handleSelect(item)">
            <span class="avatar">
              <cs-avatar :src="item.avt" alt="" :label="getFirstCharacterName(item.name)"/>
            </span>
            <span class="name">
              <code>{{item.code}}</code> - <strong>{{item.name}}</strong> - {{item.phone}}
            </span>
          </li>
          <template v-if="loading">
            <div class="loading">
              <Icon icon="svg-spinners:blocks-shuffle-3" />
            </div>
          </template>
        </ul>
        <div class="flex w-full justify-center text-[#dadada]" v-else>
          <template v-if="loading">Đang tìm kiếm...</template>
          <template v-else>Không có kết quả phù hợp.</template>
        </div>
        <paginator
          v-if="totalPage > 1"
          :page-size="10"
          :total="totalRecords"
          :sibling-count="2"
          :default-page="1"
          @page-changed="handleChangePage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
